import React, { useContext, useEffect, useReducer, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import MainContainer from "../../components/MainContainer";
import { toast } from "react-toastify";
import { KTSVG } from "../../../_metronic/helpers";
import ConfirmationModal from "../../components/ConfirmationModal";
import OpeningHourModal from "../../components/OpeningHour/Modal";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import {
  Container,
  Switch,
  Grid,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Button,
  IconButton,
  Collapse,
  Box,
} from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import socket from "../../hooks/useSocket";

const reducer = (state, action) => {
  if (action.type === "LOAD_OPENINGHOURS") {
    const openingHours = action.payload;
    const newOpeningHours = [];

    openingHours.forEach((openingHour) => {
      const openingIndex = state.findIndex((q) => q.id === openingHour.id);
      if (openingIndex !== -1) {
        state[openingIndex] = openingHour;
      } else {
        newOpeningHours.push(openingHour);
      }
    });

    return [...state, ...newOpeningHours];
  }

  if (action.type === "UPDATE_OPENINGHOURS") {
    const openingHour = action.payload;
    const openingIndex = state.findIndex((q) => q.id === openingHour.id);

    if (openingIndex !== -1) {
      state[openingIndex] = openingHour;
      return [...state];
    } else {
      return [openingHour, ...state];
    }
  }

  if (action.type === "DELETE_OPENINGHOURS") {
    const openingHourId = action.payload;
    const openingIndex = state.findIndex((q) => q.id === openingHourId);
    if (openingIndex !== -1) {
      state.splice(openingIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  modalStyle: {
    minWidth: "310px",
    width: "auto",
    margin: "20px 10px!important",
    padding: "10px",
    background: "#fff",
    borderRadius: "10px",
    maxWidth: "calc(100% - 20px)!important",
    "@media (max-width: 740px)": {
      padding: "0px !important",
      marginTop: "65px",
    },
  },
  collapseCell: {
    paddingBottom: 0,
    paddingTop: 0,
    borderBottom: "none",
  },
  expandedRow: {
    backgroundColor: "#f9f9f9",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  tableCell: {
    padding: "8px 16px",
    verticalAlign: "middle",
    borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
  },
  timeCell: {
    textAlign: "center",
    width: "100px",
  },
  expandedTableContainer: {
    padding: "16px 50px",
    backgroundColor: "#f8f9fa",
  },
  expandedTable: {
    width: "100%",
    border: "1px solid #dee2e6",
    borderRadius: "4px",
    overflow: "hidden",
  },
  expandedTableHeader: {
    backgroundColor: "#e9ecef",
    fontWeight: "bold",
  },
  expandedTableCell: {
    padding: "8px 16px",
    verticalAlign: "middle",
    borderBottom: "1px solid #dee2e6",
    fontSize: "0.875rem",
  },
  emptyCell: {
    width: "50px",
  },
  daysCount: {
    color: "#666",
    fontSize: "0.8rem",
    marginLeft: "5px",
  },
}));

const getSafeSettings = (settings) => {
  if (!settings) return {};
  if (typeof settings === "object") return settings;
  try {
    return JSON.parse(settings);
  } catch {
    return {};
  }
};

const OpeningHours = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [openingHours, dispatch] = useReducer(reducer, []);
  const [selectedHours, setSelectedHours] = useState(null);
  const [openingHourModalOpen, setOpeningHourModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const { user } = useContext(AuthContext);

  const adminId = user.adminId;
  const [getCreatedBy, setSelectedUserFilter] = useState("");

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, getCreatedBy]);

  useEffect(() => {
    setLoading(true);
    let isMounted = true;
    const abortController = new AbortController();

    const delayDebounceFn = setTimeout(() => {
      const fetchOpeningHours = async () => {
        try {
          const { data } = await api.get("/openingHour/", {
            params: { searchParam, pageNumber, getCreatedBy },
            signal: abortController.signal,
          });
          if (!isMounted) return;
          dispatch({ type: "LOAD_OPENINGHOURS", payload: data.openingHours });
          setHasMore(!!data?.hasMore);
          setLoading(false);
        } catch (err) {
          if (!isMounted || err.name === "AbortError") return;
          console.error({ err });
          toastError(err);
        }
      };
      fetchOpeningHours();
    }, 500);

    return () => {
      isMounted = false;
      abortController.abort();
      clearTimeout(delayDebounceFn);
    };
  }, [searchParam, pageNumber, getCreatedBy]);

  useEffect(() => {
    const openingHoursEventName = `openingHours-${adminId}`;
    const openingHoursEventFunction = async (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_OPENINGHOURS", payload: data.openingHour });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_OPENINGHOURS", payload: +data.openingHourId });
      }
    };
    socket.on(openingHoursEventName, openingHoursEventFunction);

    return () => {
      socket.off(openingHoursEventName, openingHoursEventFunction);
    };
  }, [adminId]);

  const formatWeekDays = (openingHour) => {
    const settings = getSafeSettings(openingHour.settings);
    if (!settings.dailyHours) return [];

    const daysMap = {
      sun: "Domingo",
      mon: "Segunda",
      tue: "Terça",
      wed: "Quarta",
      thu: "Quinta",
      fri: "Sexta",
      sat: "Sábado",
    };

    return Object.entries(settings.dailyHours)
      .filter(([_, daySettings]) => daySettings?.active)
      .map(([day, daySettings]) => ({
        day: daysMap[day] || day,
        startTime: daySettings.start || settings.startTime || "09:00",
        endTime: daySettings.end || settings.endTime || "18:00",
        breakStart: daySettings.breakStart || "-",
        breakEnd: daySettings.breakEnd || "-",
      }));
  };

  const getMainHours = (openingHour) => {
    const settings = getSafeSettings(openingHour.settings);
    return {
      startTime: settings.startTime || "",
      endTime: settings.endTime || "",
    };
  };

  const toggleRowExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleOpenOpeningHourModal = () => {
    setSelectedHours(null);
    setOpeningHourModalOpen(true);
  };

  const handleCloseOpeningHourModal = () => {
    setSelectedHours(null);
    setOpeningHourModalOpen(false);
  };

  const handleEditHours = (openingHour) => {
    setSelectedHours(openingHour);
    setOpeningHourModalOpen(true);
  };

  const handleDeleteHourModal = (openingHour) => {
    setSelectedHours(openingHour);
    setConfirmModalOpen(true);
  };

  const handleDisableHours = async (openingData) => {
    try {
      openingData = { ...openingData, status: !openingData.status };
      await api.put(`/openingHour/${openingData.id}`, openingData);
      toast.success(i18n.t("openingHourModal.success"));
    } catch (err) {
      console.error({ err });
      toastError(err);
    }
  };

  const handleDeleteHours = async () => {
    try {
      await api.delete(`/openingHour/${selectedHours.id}`);
      toast.success(i18n.t("openingHourModal.invalidMessages.deleted"));
      setConfirmModalOpen(false);
    } catch (err) {
      console.error({ err });
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("openingHours.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteHours}
      >
        {i18n.t("openingHours.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <OpeningHourModal
        open={openingHourModalOpen}
        onClose={handleCloseOpeningHourModal}
        aria-labelledby="form-dialog-title"
        openingHourId={selectedHours?.id}
      />

      <Container className={classes.modalStyle}>
        <Grid container spacing={5}>
          <Grid item sm={12} xs={12} mb={2}>
            <Typography variant="h6">{i18n.t("openingHours.title")}</Typography>
            <Button
              style={{ marginLeft: "1.5px", float: "right" }}
              variant="contained"
              color="primary"
              onClick={handleOpenOpeningHourModal}
            >
              <KTSVG
                path="media/icons/duotune/arrows/arr075.svg"
                className="svg-icon-3"
              />
              {i18n.t("openingHours.buttons.add")}
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={5}>
          <Grid item sm={12} xs={12} mb={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.emptyCell}></TableCell>
                  <TableCell>{i18n.t("openingHours.table.name")}</TableCell>
                  <TableCell>{i18n.t("openingHours.table.days")}</TableCell>
                  <TableCell>{i18n.t("openingHours.table.start")}</TableCell>
                  <TableCell>{i18n.t("openingHours.table.end")}</TableCell>
                  <TableCell>
                    {i18n.t("openingHours.table.breakStart")}
                  </TableCell>
                  <TableCell>{i18n.t("openingHours.table.breakEnd")}</TableCell>
                  <TableCell>
                    {i18n.t("openingHours.table.updatedBy")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("openingHours.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {openingHours.map((openingHour) => {
                  const days = formatWeekDays(openingHour);
                  const mainHours = getMainHours(openingHour);
                  const isExpanded = expandedRows[openingHour.id];

                  return (
                    <React.Fragment key={openingHour.id}>
                      <TableRow
                        className={isExpanded ? classes.expandedRow : ""}
                      >
                        <TableCell className={classes.emptyCell}>
                          {days.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpand(openingHour.id)}
                              style={{ padding: "5px" }}
                            >
                              {isExpanded ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>{openingHour?.name || ""}</TableCell>
                        <TableCell>
                          {days.length > 0 ? days[0].day : "-"}
                          {days.length > 1 && (
                            <span className={classes.daysCount}>
                              (+{days.length - 1})
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {days.length > 0
                            ? days[0].startTime
                            : mainHours.startTime || "-"}
                        </TableCell>
                        <TableCell>
                          {days.length > 0
                            ? days[0].endTime
                            : mainHours.endTime || "-"}
                        </TableCell>
                        <TableCell>
                          {days.length > 0 ? days[0].breakStart : "-"}
                        </TableCell>
                        <TableCell>
                          {days.length > 0 ? days[0].breakEnd : "-"}
                        </TableCell>
                        <TableCell>{openingHour?.["User.name"]}</TableCell>
                        <TableCell align="center">
                          <Button
                            title={i18n.t("buttons.edit")}
                            className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                            onClick={() => handleEditHours(openingHour)}
                          >
                            <KTSVG
                              path="/media/icons/duotune/art/art005.svg"
                              className="svg-icon-3"
                            />
                          </Button>
                          <Button
                            title={i18n.t("buttons.delete")}
                            className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                            onClick={() => handleDeleteHourModal(openingHour)}
                            >
                            <KTSVG
                              path="/media/icons/duotune/general/gen027.svg"
                              className="svg-icon-3"
                            />
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Linhas expandidas */}
                      {days.length > 1 && (
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            className={classes.collapseCell}
                            style={{ padding: 0 }}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box className={classes.expandedTableContainer}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Dias adicionais
                                </Typography>
                                <Table className={classes.expandedTable}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell className={classes.expandedTableHeader}>
                                        {i18n.t("openingHours.tableCollapse.days")}
                                      </TableCell>
                                      <TableCell className={classes.expandedTableHeader}>
                                        {i18n.t("openingHours.tableCollapse.start")}
                                      </TableCell>
                                      <TableCell className={classes.expandedTableHeader}>
                                        {i18n.t("openingHours.tableCollapse.end")}
                                      </TableCell>
                                      <TableCell className={classes.expandedTableHeader}>
                                        {i18n.t("openingHours.tableCollapse.breakStart")}
                                      </TableCell>
                                      <TableCell className={classes.expandedTableHeader}>
                                        {i18n.t("openingHours.tableCollapse.breakEnd")}
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {days.slice(1).map((day, index) => (
                                      <TableRow key={`${openingHour.id}-${index}`}>
                                        <TableCell className={classes.expandedTableCell}>
                                          {day.day}
                                        </TableCell>
                                        <TableCell className={classes.expandedTableCell}>
                                          {day.startTime}
                                        </TableCell>
                                        <TableCell className={classes.expandedTableCell}>
                                          {day.endTime}
                                        </TableCell>
                                        <TableCell className={classes.expandedTableCell}>
                                          {day.breakStart}
                                        </TableCell>
                                        <TableCell className={classes.expandedTableCell}>
                                          {day.breakEnd}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </Container>
    </MainContainer>
  );
};

export default OpeningHours;